package songwritersnotebook.app.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.animation.Crossfade
import androidx.compose.material.icons.filled.Save
import androidx.compose.material.icons.filled.CloudUpload
import androidx.compose.material.icons.filled.CloudDone
import androidx.compose.material.icons.filled.CloudOff
import androidx.compose.foundation.clickable
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import songwritersnotebook.app.utils.NetworkUtils
import songwritersnotebook.app.viewmodels.SaveStatus

@Composable
fun SaveStatusIndicator(
    saveStatus: SaveStatus,
    isCloudSyncEnabled: Boolean = true,
    onClick: () -> Unit = {},
    showText: Boolean = true
) {
    val isOnline by remember { NetworkUtils().networkStatus }.collectAsState(initial = true)
    
    // Prioritize OFFLINE status if network is lost, unless we are actively SAVING
    val effectiveStatus = if (!isOnline && saveStatus != SaveStatus.SAVING) {
        SaveStatus.OFFLINE
    } else {
        saveStatus
    }

    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .padding(start = 8.dp)
            .clickable(enabled = effectiveStatus == SaveStatus.ERROR || effectiveStatus == SaveStatus.SYNCING || effectiveStatus == SaveStatus.IDLE) { onClick() }
    ) {
        Crossfade(targetState = effectiveStatus) { status ->
            when (status) {
                SaveStatus.ERROR -> {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            imageVector = Icons.Default.CloudUpload,
                            contentDescription = "Saved locally, sync error - Tap to retry",
                            modifier = Modifier.size(14.dp),
                            tint = Color.Red
                        )
                        // Always show text for errors even if showText is false (for Editor)
                        Spacer(modifier = Modifier.width(4.dp))
                        Text(
                            text = "Saved locally, sync error",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.Red
                        )
                    }
                }
                SaveStatus.SYNCING -> {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            imageVector = Icons.Default.CloudUpload,
                            contentDescription = "Sync pending - Tap to save",
                            modifier = Modifier.size(14.dp),
                            tint = MaterialTheme.colorScheme.primary
                        )
                        if (showText) {
                            Spacer(modifier = Modifier.width(4.dp))
                            Text(
                                text = "Sync pending",
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
                SaveStatus.SAVING -> {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(12.dp),
                            strokeWidth = 2.dp,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        if (showText) {
                            Spacer(modifier = Modifier.width(4.dp))
                            Text(
                                text = "Saving...",
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
                SaveStatus.SAVED, SaveStatus.IDLE -> {
                    val icon = if (isCloudSyncEnabled) Icons.Default.CloudDone else Icons.Default.Save
                    val text = if (isCloudSyncEnabled) "Saved to cloud" else "Saved locally"
                    
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            imageVector = icon,
                            contentDescription = text,
                            modifier = Modifier.size(14.dp),
                            tint = MaterialTheme.colorScheme.primary
                        )
                        if (showText) {
                            Spacer(modifier = Modifier.width(4.dp))
                            Text(
                                text = text,
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
                SaveStatus.OFFLINE -> {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            imageVector = Icons.Default.CloudOff,
                            contentDescription = "Saved locally",
                            modifier = Modifier.size(14.dp),
                            tint = MaterialTheme.colorScheme.secondary
                        )
                        if (showText) {
                            Spacer(modifier = Modifier.width(4.dp))
                            Text(
                                text = "Saved locally",
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
        }
    }
}
